// © 2025 GridStorm / Tekivex — All Rights Reserved
// Unauthorized reproduction or distribution is prohibited.
/**
 * Flexible AI enrichment.
 *
 * Enrichment is pluggable: the Sentinel accepts ANY object implementing
 * `EnrichProvider`. Ship your own (call a private endpoint, a local model, or
 * even a non-LLM heuristic), or use the built-in `createLlmEnrichProvider` for
 * OpenAI / Anthropic / Gemini / Ollama / any OpenAI-compatible endpoint.
 *
 * Enrichment is always best-effort: if a provider throws or times out, the
 * deterministic Finding stands unchanged.
 */

import type { Finding } from './types.js';

export interface Enrichment {
  cause?:           string;
  suggestedAction?: string;
}

export interface EnrichInput {
  /** The deterministic finding to improve. */
  finding:     Finding;
  /** Compact JSON anomaly context (from enrichmentContext()). */
  contextJson: string;
}

/** Implement this to plug in any AI (or non-AI) enrichment. */
export interface EnrichProvider {
  readonly name: string;
  enrich(input: EnrichInput, signal: AbortSignal): Promise<Enrichment>;
}

// ─── Built-in LLM provider ───────────────────────────────────────────────────

export type LlmKind = 'openai' | 'anthropic' | 'gemini' | 'ollama' | 'openai-compatible';

export interface LlmEnrichConfig {
  kind:        LlmKind;
  /** API key (not needed for ollama). */
  apiKey?:     string;
  /** Model id, e.g. "gpt-4o-mini", "claude-3-5-sonnet-latest". */
  model:       string;
  /** Endpoint override; sensible default per kind if omitted. */
  endpoint?:   string;
  /** Optional system-prompt override. */
  systemPrompt?: string;
  /** Request timeout (ms). Default 12000. */
  timeoutMs?:  number;
}

const DEFAULT_ENDPOINTS: Record<LlmKind, string> = {
  'openai':            'https://api.openai.com/v1/chat/completions',
  'anthropic':         'https://api.anthropic.com/v1/messages',
  'gemini':            'https://generativelanguage.googleapis.com/v1beta/models',
  'ollama':            'http://localhost:11434/api/chat',
  'openai-compatible': 'http://localhost:8000/v1/chat/completions',
};

const SYSTEM_PROMPT =
  'You are a senior monitoring analyst. You are given an anomaly and its statistics. ' +
  'Write a concise, plain-English explanation of what likely happened and a sharp, ' +
  'specific next action. Be quantitative and avoid filler. ' +
  'Respond with ONLY a JSON object: {"cause": string, "action": string}. ' +
  'Keep "cause" under 320 characters and "action" under 160 characters.';

/** Build an EnrichProvider backed by a standard LLM endpoint. */
export function createLlmEnrichProvider(cfg: LlmEnrichConfig): EnrichProvider {
  const timeoutMs = cfg.timeoutMs ?? 12_000;
  const system = cfg.systemPrompt?.trim() || SYSTEM_PROMPT;

  return {
    name: `llm:${cfg.kind}:${cfg.model}`,
    async enrich(input, outerSignal) {
      if (cfg.kind !== 'ollama' && !(cfg.apiKey ?? '').trim()) return {};

      const userPrompt =
        `Anomaly context (JSON):\n${input.contextJson}\n\n` +
        `A statistical baseline explanation is:\n"${input.finding.cause}"\n\n` +
        `Rewrite it as {"cause","action"} JSON.`;

      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      const onAbort = () => ctrl.abort();
      outerSignal.addEventListener('abort', onAbort);
      try {
        const text = await callLlm(cfg, system, userPrompt, ctrl.signal);
        const parsed = extractJson(text);
        if (!parsed) return {};
        const out: Enrichment = {};
        if (typeof parsed.cause === 'string' && parsed.cause.trim())   out.cause = parsed.cause.trim();
        if (typeof parsed.action === 'string' && parsed.action.trim()) out.suggestedAction = parsed.action.trim();
        return out;
      } catch {
        return {};
      } finally {
        clearTimeout(timer);
        outerSignal.removeEventListener('abort', onAbort);
      }
    },
  };
}

// ─── transport per provider kind ─────────────────────────────────────────────

async function callLlm(cfg: LlmEnrichConfig, system: string, user: string, signal: AbortSignal): Promise<string> {
  const endpoint = cfg.endpoint?.trim() || DEFAULT_ENDPOINTS[cfg.kind];
  const model = cfg.model.trim();
  const apiKey = (cfg.apiKey ?? '').trim();

  if (cfg.kind === 'anthropic') {
    const res = await fetch(endpoint, {
      method: 'POST', signal,
      headers: {
        'content-type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({ model, max_tokens: 512, system, messages: [{ role: 'user', content: user }] }),
    });
    const j = await res.json();
    return (j?.content?.[0]?.text as string) ?? '';
  }

  if (cfg.kind === 'gemini') {
    const url = `${endpoint.replace(/\/$/, '')}/${model}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const res = await fetch(url, {
      method: 'POST', signal,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        systemInstruction: { parts: [{ text: system }] },
        contents: [{ role: 'user', parts: [{ text: user }] }],
        generationConfig: { responseMimeType: 'application/json' },
      }),
    });
    const j = await res.json();
    return (j?.candidates?.[0]?.content?.parts?.[0]?.text as string) ?? '';
  }

  if (cfg.kind === 'ollama' && endpoint.includes('/api/chat')) {
    const res = await fetch(endpoint, {
      method: 'POST', signal,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        model, stream: false, format: 'json',
        messages: [{ role: 'system', content: system }, { role: 'user', content: user }],
      }),
    });
    const j = await res.json();
    return (j?.message?.content as string) ?? '';
  }

  // openai, openai-compatible, or ollama pointed at an OpenAI-style path
  const body: Record<string, unknown> = {
    model, temperature: 0.3,
    messages: [{ role: 'system', content: system }, { role: 'user', content: user }],
  };
  if (cfg.kind === 'openai') body.response_format = { type: 'json_object' };
  const res = await fetch(endpoint, {
    method: 'POST', signal,
    headers: { 'content-type': 'application/json', ...(apiKey ? { authorization: `Bearer ${apiKey}` } : {}) },
    body: JSON.stringify(body),
  });
  const j = await res.json();
  return (j?.choices?.[0]?.message?.content as string) ?? '';
}

function extractJson(text: string): { cause?: unknown; action?: unknown } | null {
  if (!text) return null;
  const cleaned = text.replace(/```json\s*/gi, '').replace(/```/g, '');
  const start = cleaned.indexOf('{');
  const end = cleaned.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  try { return JSON.parse(cleaned.slice(start, end + 1)); }
  catch { return null; }
}
